import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import { getCommonColumns } from 'csam/admin/components/CommonColumns';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';

const EmailTemplates = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const deleteMutation = useAuthenticatedMutation<string[], void>(['delete-template'], (ids) => ({
    url: `${fixed}admin/${locale}/email_templates`,
    method: 'DELETE',
    data: { ids },
  }));

  const queryClient = useQueryClient();

  const handleDelete = (ids?: string[]) => {
    const idsToDelete = ids || selectedIds;
    deleteMutation.mutate(idsToDelete.map((id) => id.toString()) as unknown as void, {
      onSuccess: () => {
        toast.success(`Record Deleted Successfully`);
        queryClient.invalidateQueries({
          queryKey: ['EmailTemplatePage', 'EmailTemplatePageData', locale],
        });
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      filterable: false,
      width: 40,
      renderCell: (params) => params.value,
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      renderCell: (params) => (Number(params.value) === 1 ? 'Active' : 'Inactive'),
    },
    { field: 'template_title', headerName: 'Template Title', width: 200 },
    { field: 'email_subject', headerName: 'Email Subject', width: 200 },
    { field: 'email_body', headerName: 'Email Body', width: 200 },
    {
  field: 'template_link',
  headerName: 'Template link',
  width: 250,
  renderCell: (params) => {
    if (!params.value) return '';

    const linkParts = params.value.split('/');
    
    // Replace locale (assuming it's always after domain)
    linkParts[3] = locale;  

    const updatedLink = linkParts.join('/');

    return (
      <a href={updatedLink} target="_blank" rel="noopener noreferrer">
        {updatedLink}
      </a>
    );
  },
},
    ...getCommonColumns({ handleDelete }),
  ];

  interface EmailTemplateData {
    id: number;
    template_title: string;
    email_subject: string;
    email_body: string;
    created_at: string;
    status: number;
  }

  interface TemplateResponse {
    success: boolean;
    data: EmailTemplateData;
    translatableFields: string[];
  }

  const relations = ['year'];
  const relationsParam = relations.join(',');

  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<TemplateResponse[]>(
    ['EmailTemplatePage', 'EmailTemplatePageData', locale],
    {
      url: `${fixed}admin/${locale || localeFromStorage}/email_templates?relations=${relationsParam}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (isPending) return <AdminPageLoader />;
  if (error) return <div>Error: {error.message}</div>;
  if (data && !data.success) return <AdminInternalError />;

  return (
    <AdminWrapper>
      <AdminPageHeader title="Email Templates" locale={locale} setLocale={setLocale} createLink="create" enableBulkDelete />
      <AdminDataGrid<TemplateResponse> data={data} columns={columns} setSelectedIds={setSelectedIds} />
    </AdminWrapper>
  );
};

export default EmailTemplates;
